<script setup>
import { usePageHead } from '@composables/use-page-head'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAppearanceStore } from '@state/stores/appearance'
import { useMemberStore } from '@state/stores/member'
import { usePremiumStore } from '@state/stores/premium'
import { useMessages } from '@composables/use-messages'
import DataLayer from '@services/data-layer/index'
import formatDate from './../../../utils/format-date-full'

const premiumService = DataLayer.subscriptions

const appearanceStore = useAppearanceStore()
const memberStore = useMemberStore()
const premiumStore = usePremiumStore()
const router = useRouter()
const getMessageText = useMessages()

const crumbs = [
    { label: 'Account Management', to: '/account' },
    { label: 'Cancel Premium' },
]

const isLoading = ref(false)

const user = computed(() => memberStore.currentMember)
const currentSubscription = computed(() => premiumStore.currentSubscription)

const handleSubscriptionUpdate = async () => {
    const subscriptions = await premiumService.getSubscriptionsByMemberId(user.value.id)
    premiumStore.setSubscriptions(subscriptions.body)
    const activeSubscription = premiumStore.activeSubscription

    let transaction = null
    if (activeSubscription) {
        try {
            transaction = await premiumService.getCheckoutById(activeSubscription.subscriptionsCheckoutId.id)
        } catch (error) {
            // The cancellation itself already succeeded — a broken checkout
            // record only costs us the payment-method display.
            console.error('Failed to reload the checkout after cancellation:', error)
        }
    }
    premiumStore.setActiveTransaction(transaction ? transaction.body : null)
}

const cancelSubscription = async () => {
    isLoading.value = true
    try {
        await premiumService.cancelSubscription(currentSubscription.value)
        await handleSubscriptionUpdate()
        router.push('/account/cancellation-feedback')
        isLoading.value = false
    } catch (error) {
        appearanceStore.handleNotification({
            message: getMessageText('SOMETHING_WENT_WRONG'),
            type: 'danger',
        })
        isLoading.value = false
    }
}

const onKeepPremium = () => {
    router.push('/account')
}

usePageHead({ title: 'Cancel Premium', description: 'Cancel Premium' })
</script>

<template>
    <div class="cancel-premium-container">
        <BaseBreadcrumbs :crumbs="crumbs"/>
        <div class="main-container text-center">
            <div class="title">Are you sure?</div>
            <div class="subtitle fw-medium">
                If you cancel, you'll change to our free plan on {{ formatDate(new Date(currentSubscription.endTime)) }}.
                <br>
                Here's how your listening will change on that date:
            </div>
            <div class="points">
                <div class="point">
                    <BaseIcon
                        class="point-icon"
                        name="disc"
                    />
                    <div class="point-description fw-medium">No more hi-res audio</div>
                </div>
                <div class="point">
                    <BaseIcon
                        class="point-icon"
                        name="download"
                    />
                    <div class="point-description fw-medium">
                        No more downloads
                        <br>
                        on our mobile apps to listen offline
                    </div>
                </div>
                <div class="point">
                    <BaseIcon
                        class="point-icon"
                        name="play"
                    />
                    <div class="point-description fw-medium">No more on-demand playback</div>
                </div>
                <div class="point">
                    <div class="mixes-count-number fw-bolder">100,000</div>
                    <div class="point-description fw-medium">
                        You'll no longer have instant access to over 100,000
                        <br>
                        exclusive mixes
                    </div>
                </div>
            </div>
            <div class="actions">
                <BaseButton
                    class="action-button"
                    button-type="tagBlack"
                    size="medium"
                    type="button"
                    @click="onKeepPremium"
                >Keep Premium</BaseButton>

                <BaseButton
                    class="action-button"
                    button-type="tagBlack"
                    size="medium"
                    type="button"
                    @click="cancelSubscription"
                >
                    <span class="button-content">Continue to cancel<BaseIcon
                        v-if="isLoading"
                        class="loading-icon spin"
                        name="spinner"
                    /></span>
                </BaseButton>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.cancel-premium-container {
    padding-bottom: $s-h;
}

.main-container {
    margin-top: $s-l;
}

.title {
    @extend %h1;
}

.subtitle {
    font-size: 14px;
    line-height: 150%;
}

.points {
    display: flex;
    flex-direction: column;
    gap: $s-l;
    margin-top: $s-xl;
}

.point {
    display: flex;
    flex-direction: column;
    gap: $s-xs;
    align-items: center;
}

.point-icon {
    // Explicit size: BaseIcon now renders a 24px attr, so max-* alone no
    // longer grows these pictogram-scale glyphs to the old rendering.
    @include square(100px);
    @include iconStroke(100px);

    color: $c-white;
}

.point-description {
    font-size: 14px;
    line-height: 150%;
}

.mixes-count-number {
    font-size: 48px;
}

.actions {
    display: flex;
    align-items: center;
    justify-content: space-around;
    margin-top: $s-l;

    .action-button {
        padding: $s-m $s-l;
        font-weight: unset;
        text-transform: unset;
    }
}

.button-content {
    display: flex;
    gap: $s-m;
    align-items: center;
}

.loading-icon {
    max-width: 20px;
    max-height: 20px;
    color: $c-white;
}

@include tablet {
    .subtitle {
        font-size: 16px;
    }

    .points {
        gap: $s-xxl;
        margin-top: $s-h;
    }

    .point {
        gap: $s-s;
    }

    .point-icon {
        @include square(140px);
        @include iconStroke(140px);
    }

    .point-description {
        font-size: 16px;
    }

    .mixes-count-number {
        font-size: 96px;
    }

    .actions {
        margin-top: $s-xxl;
    }
}
</style>
